import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import { Button, Container, Form, Table, Card, Row, Col, ProgressBar, Badge } from "react-bootstrap";
import { API_ENDPOINTS } from './config/api';
import PageTitle from './components/PageTitle';
import { getRatingOptions } from "./utils/ratingScales";

const QuestionRenderer = React.memo(({ question, answer }) => {
  const renderAnswer = useCallback((question, answer) => {
    if (!question || !question.questionType) {
      return null;
    }

    try {
      switch (question.questionType) {
        case "header-paragraph":
          try {
            const parsed = JSON.parse(question.questionText);
            return (
              <div>
                <div style={{ 
                  fontSize: parsed.size === "h1" ? "32px" : parsed.size === "h2" ? "24px" : "20px",
                  fontWeight: "bold",
                  marginBottom: "10px",
                  color: "var(--text-dark)"
                }}>
                  {parsed.header}
                </div>
                <div style={{ 
                  marginBottom: "20px",
                  color: "var(--muted-text)",
                  lineHeight: "1.5"
                }}>
                  {parsed.paragraph}
                </div>
              </div>
            );
          } catch (e) {
            return <p>{question.questionText}</p>;
          }

        case "text":
          return <div className="p-3 rounded-3 bg-light">{answer || "—"}</div>;

        case "textarea":
          return (
            <div className="p-3 rounded-3 bg-light">{answer || "—"}</div>
          );

        case "radio-matrix":
          return (
            <div className="table-responsive custom-modern-responsive">
            <Table>
              <thead>
                <tr>
                  <th className="px-3">Performance Factor</th>
                  {(question.columns && question.columns.length > 0
                    ? question.columns
                    : [
                        "Unsatisfactory",
                        "Needs Improvement",
                        "Meets Expectations",
                        "Exceeds Expectations",
                      ]
                  ).map((col, colIdx) => (
                    <th key={colIdx}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {question.options.map((opt, idx) => (
                  <tr key={idx}>
                    <td className="px-3">{opt}</td>
                    {(question.columns && question.columns.length > 0
                      ? question.columns
                      : [
                          "Unsatisfactory",
                          "Needs Improvement",
                          "Meets Expectations",
                          "Exceeds Expectations",
                        ]
                    ).map((_, colIdx) => (
                      <td
                        key={colIdx}
                        style={{
                          boxShadow: answer && answer[idx] === colIdx ? 'inset 0 0 0 9999px var(--gray-300)': '',
                          borderRadius: answer && answer[idx] === colIdx ? '0px' : '',
                        }}
                      >
                        <div
                          className="rounded-circle"
                          style={{
                            width: 16,
                            height: 16,
                            margin: "0 auto",
                            backgroundColor: answer && answer[idx] === colIdx ? 'var(--blue)' : '',
                            border: answer && answer[idx] === colIdx ? '1px solid var(--blue)' : ' 1px solid var(--gray-400)',
                          }}
                        >
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              margin: '3px auto',
                              width: '8px',
                              borderRadius: '50%',
                              height: '8px',
                              backgroundColor: answer && answer[idx] === colIdx ? 'var(--white)' : '',
                            }}
                          > 
                          </span>
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table></div>
          );

        case "checkbox":
          return (
            <div className="d-flex flex-column gap-2">
              {question.options.map((option, idx) => (
                <Button
                  key={idx}
                  variant={answer && answer.includes(option) ? "subtle" : "light-solid"}
                  className="p-3 d-flex align-items-center gap-2"
                  disabled
                >
                  <Form.Check
                    type="checkbox"
                    checked={answer && answer.includes(option)}
                    disabled
                    onChange={() => {}}
                    style={{ pointerEvents: "none" }}
                  />
                  {option}
                </Button>
              ))}
            </div>
          );

        case "radio":
          return (
            <div className="d-flex flex-column gap-2">
              {question.options.map((option, idx) => (
                <Button
                  key={idx}
                  variant={answer === option ? "subtle" : "light-solid"}
                  className="p-3 d-flex align-items-center gap-2"
                  disabled
                >
                  <Form.Check
                    type="radio"
                    label={<span>{option}</span>}
                    checked={answer === option}
                    disabled
                    className="mb-0"
                  />
                </Button>
              ))}
            </div>
          );

        case "dropdown":
          return (
            <Form.Select disabled value={answer || ""}>
              <option value="">Select an option</option>
              {question.options.map((option, idx) => (
                <option key={idx} value={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
          );

        case "rating":
          return (
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {getRatingOptions(question).map((opt) => (
                <Button
                  key={String(opt.value)}
                  variant={String(answer) === String(opt.value) ? "subtle" : "light-solid"}
                  className="p-3 d-flex align-items-center gap-2 w-100"
                  disabled
                >
                  <Form.Check
                    type="radio"
                    checked={String(answer) === String(opt.value)}
                    disabled
                    onChange={() => {}}
                    style={{ pointerEvents: "none" }}
                    className="m-0"
                  />
                  <span>{opt.label}</span>
                </Button>
              ))}
            </div>
          );

        default:
          return (
            <div className="p-3 rounded-3 bg-light">
              {JSON.stringify(answer)}
            </div>
          );
      }
    } catch (error) {
      console.error("Error rendering answer:", error);
      return <p>Error rendering answer</p>;
    }
  }, []);

  return renderAnswer(question, answer);
});

const EvaluationResponseView = ({
  responseId: responseIdProp,
  apiBase = API_ENDPOINTS.DIRECTOR,
  responseEndpoint = "evaluation-response",
  embedded = false,
  hidePageChrome = false,
  backPath = "/evaluations",
}) => {
  const { id: routeId } = useParams();
  const id = responseIdProp || routeId;
  const navigate = useNavigate();
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const questionRefs = useRef({});

  useEffect(() => {
    let isMounted = true;

    const fetchResponse = async () => {
      try {
        const res = await fetch(`${apiBase}/${responseEndpoint}/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!res.ok) {
          throw new Error("Failed to fetch evaluation response");
        }

        const data = await res.json();
        if (isMounted) {
          setResponse(data);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchResponse();

    return () => {
      isMounted = false;
    };
  }, [id, apiBase, responseEndpoint]);

  // Helper: get all numbered questions (exclude header-paragraph and divider)
  const numberedQuestions = response?.template?.questions?.filter(q => !["header-paragraph", "divider"].includes(q.questionType)) || [];

  // Helper: check if a question is filled out (answered)
  const isQuestionFilled = (q) => {
    if (!response?.answers) return false;
    const answer = response.answers[q.id];
    if (q.questionType === "checkbox") {
      return Array.isArray(answer) && answer.length > 0;
    }
    if (q.questionType === "radio-matrix") {
      return answer && Object.keys(answer).length === q.options.length;
    }
    return answer !== undefined && answer !== "" && answer !== null;
  };

  const answeredCount = numberedQuestions.filter(isQuestionFilled).length;
  const totalQuestions = numberedQuestions.length;
  const progressPercent =
    totalQuestions > 0
      ? Math.round((answeredCount / totalQuestions) * 100)
      : 0;

  const questions = response?.template?.questions || [];

  if (loading) {
    return (
      <div className={embedded ? "bg-light min-vh-0" : "bg-light min-vh-100"}>
        {!hidePageChrome && <PageTitle title="Loading..." />}
        {!hidePageChrome && <Navbar pageTitle="Evaluation Response" />}
        <div className="container py-5">
          <div className="bg-white rounded shadow-sm p-5 text-center">
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={embedded ? "bg-light min-vh-0" : "bg-light min-vh-100"}>
        {!hidePageChrome && <PageTitle title="Evaluation Error" />}
        {!hidePageChrome && <Navbar pageTitle="Evaluation Response" />}
        <div className="container py-5">
          <div className="bg-white rounded shadow-sm p-5 text-center">
            <p>Error: {error}</p>
            <button
              onClick={() => navigate(backPath)}
              className="btn btn-primary mt-4"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!response || !response.template) {
    return (
      <div className={embedded ? "bg-light min-vh-0" : "bg-light min-vh-100"}>
        {!hidePageChrome && <PageTitle title="Response Not Found" />}
        {!hidePageChrome && <Navbar pageTitle="Evaluation Response" />}
        <div className="container py-5">
          <div className="bg-white rounded shadow-sm p-5 text-center">
            <p>Evaluation response not found</p>
            <button
              onClick={() => navigate(backPath)}
              className="btn btn-primary mt-4"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? "min-vh-0" : "min-vh-100"}>
      {!hidePageChrome && (
        <>
          <PageTitle
            title={response?.template?.title || "Evaluation Response"}
            description="View evaluation response details"
          />
          <Navbar pageTitle={response?.template?.title || "Evaluation Response"} />
        </>
      )}
      <div className={embedded ? "border-bottom bg-inverse-light p-0 custom-sticky-top" : "bg-white shadow-sm custom-sticky-top"}>
        <div className={embedded ? "container-fluid px-4" : "container"}>
          <div className="d-flex flex-md-row align-items-md-center align-items-start flex-column justify-content-md-between justify-content-start gap-2">
            <div className="d-flex flex-row align-items-center justify-content-start gap-2">
              <h5 className="fw-semibold mb-0 py-md-3 py-2">
                {response?.template?.title || "Evaluation Response"}
              </h5>
              <span className={`badge ${
                response?.template?.evaluationType === 'performance' ? 'bg-primary' : 
                response?.template?.evaluationType === 'feedback' ? 'bg-info' :
                response?.template?.evaluationType === 'survey' ? 'bg-success' :
                response?.template?.evaluationType === 'questionnaire' ? 'bg-warning' :
                'bg-secondary'
              }`}>
                {response?.template?.evaluationType || response?.template?.type || "Evaluation"}
              </span>
            </div>
            <div className="d-flex py-md-0 py-2 flex-row align-items-center justify-content-end gap-3">
              <Button
                variant="light-solid"
                size="sm"
                onClick={() => setShowDetails(!showDetails)}
                className="d-flex align-items-center gap-2"
              >
                <span className="d-none d-md-inline">{showDetails ? 'Hide' : 'Show'} Details</span>
                <i className={`bi ${showDetails ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
              </Button>
            </div>
          </div>
          {showDetails && (
            <div className="border-top pt-3 pb-3">
              <Row className="g-3">
                {response.evaluation?.evaluator && response.evaluation.evaluator.firstName ? (
                  <Col md={4} lg={4} className="col-6">
                    <div>
                      <span className="text-muted fw-bold small d-block mb-1">Evaluator:</span>
                      <div>
                        <span className="text-muted">{response.evaluation.evaluator.firstName} {response.evaluation.evaluator.lastName}</span>
                        {response.evaluation.evaluator.role && (
                          <Badge bg="secondary" className="ms-2">
                            {response.evaluation.evaluator.role}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </Col>
                ) : null}
                {response.resident && (
                  <Col md={4} lg={4} className="col-6">
                    <div>
                      <span className="text-muted fw-bold small d-block mb-1">Evaluating:</span>
                      <div>
                        <p className="mb-0 text-muted">{response.resident.firstName} {response.resident.lastName}</p>
                      </div>
                    </div>
                  </Col>
                )}
                <Col md={4} lg={4} className="col-6">
                  <div>
                    <span className="text-muted fw-bold small d-block mb-1">Submitted On:</span>
                    <div>
                      <p className="mb-0 text-muted">{response.createdAt ? new Date(response.createdAt).toLocaleString() : 'Date not available'}</p>
                    </div>
                  </div>
                </Col>
                {response.updatedAt && response.updatedAt !== response.createdAt && (
                  <Col md={4} lg={4} className="col-6">
                    <div>
                      <span className="text-muted fw-bold small d-block mb-1">Last Updated:</span>
                      <div>
                        <p className="mb-0 text-muted">{new Date(response.updatedAt).toLocaleString()}</p>
                      </div>
                    </div>
                  </Col>
                )}
                <Col md={4} lg={4} className="col-6">
                  <div>
                    <span className="text-muted fw-bold small d-block mb-1">Total Questions:</span>
                    <p className="mb-0 text-muted">{totalQuestions}</p>
                  </div>
                </Col>
                {response.template?.isTimed && response.template?.timeLimit && (
                  <Col md={4} lg={4} className="col-6">
                    <div>
                      <span className="text-muted fw-bold small d-block mb-1">Time Limit:</span>
                      <div>
                        <p className="mb-0 text-muted">{response.template.timeLimit} minutes</p>
                      </div>
                    </div>
                  </Col>
                )}
                {(response.evaluation?.startTime || response.template?.startTime) && (
                  <Col md={4} lg={4} className="col-6">
                    <div>
                      <span className="text-muted fw-bold small d-block mb-1">Start Date:</span>
                      <div>
                        <p className="mb-0 text-muted">{new Date(response.evaluation?.startTime || response.template.startTime).toLocaleString()}</p>
                      </div>
                    </div>
                  </Col>
                )}
                {(response.evaluation?.endTime || response.evaluation?.dueDate || response.template?.endTime) && (
                  <Col md={4} lg={4} className="col-6">
                    <div>
                      <span className="text-muted fw-bold small d-block mb-1">End Date:</span>
                      <div>
                        <p className="mb-0 text-muted">{new Date(response.evaluation?.endTime || response.evaluation?.dueDate || response.template.endTime).toLocaleString()}</p>
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            </div>
          )}
        </div>
      </div>
      <Container fluid={embedded} className={embedded ? "p-0" : "py-4"}>
        <Row className={embedded ? "g-0 align-items-stretch" : ""}>
          <Col lg={3} md={4} className={embedded ? "d-none d-lg-block bg-inverse-light px-3 border-end" : "d-none d-md-block"}>
            <div className="position-sticky" style={{ top: 80 }}>
              {embedded ? (
                <div className="mb-3 px-2">
                  <div className="fw-bold mb-2">Progress</div>
                  <div className="mb-3">
                    <ProgressBar
                      now={progressPercent}
                      variant={
                        progressPercent === 100 ? "success-solid" : "primary-solid"
                      }
                      style={{ height: 6 }}
                      animated
                    />
                    <div className="d-flex flex-row align-items-center justify-content-between mt-2">
                      <span className="small gap-2">
                        Completion <strong className="fw-semibold">{progressPercent}%</strong>
                      </span>
                      <span className="text-muted small">
                        {answeredCount}/{totalQuestions} answered
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <Card className="mb-3 border-0 shadow-sm">
                  <Card.Header className="border-0 bg-white px-4 pt-3 fw-bold mb-2">
                    Progress
                  </Card.Header>
                  <Card.Body className="px-4 py-0">
                    <div className="mb-3">
                      <ProgressBar
                        now={progressPercent}
                        variant={
                          progressPercent === 100 ? "success-solid" : "primary-solid"
                        }
                        style={{ height: 6 }}
                        animated
                      />
                      <div className="d-flex flex-row align-items-center justify-content-between mt-2">
                        <span className="small gap-2">
                          Completion <strong className="fw-semibold">{progressPercent}%</strong>
                        </span>
                        <span className="text-muted small">
                          {answeredCount}/{totalQuestions} answered
                        </span>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {embedded ? (
                <div className="px-2">
                  <div className="fw-bold mb-2">Questions</div>
                  <ul className="list-unstyled mb-0">
                    {response?.template?.introHtml && response.template.introHtml.trim() && (
                      <li key="intro" className="mb-2">
                        <Button
                          variant="light"
                          className="d-flex align-items-center justify-content-between p-2 rounded-3 border bg-white w-100"
                          style={{ overflow: "hidden" }}
                          onClick={() => {
                            const node = questionRefs.current["intro"];
                            if (node) {
                              node.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                              });
                            }
                          }}
                        >
                          <div className="d-flex align-items-center gap-3 text-start">
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center border text-muted"
                              style={{ width: 32, height: 32 }}
                            >
                              <i className="bi bi-info-lg text-muted" />
                            </div>
                            <div>
                              <div
                                className="fw-semibold text-truncate small"
                                style={{ maxWidth: 180 }}
                              >
                                Introduction
                              </div>
                            </div>
                          </div>
                        </Button>
                      </li>
                    )}
                    {numberedQuestions.map((question, idx) => {
                      const answered = isQuestionFilled(question);
                      const qId = question.id || `question-${idx}`;
                      return (
                        <li key={qId} className="mb-2">
                          <Button
                            variant="light"
                            className="d-flex align-items-center justify-content-between p-2 rounded-3 border bg-white w-100"
                            style={{ overflow: "hidden" }}
                            onClick={() => {
                              const node = questionRefs.current[qId];
                              if (node) {
                                node.scrollIntoView({
                                  behavior: "smooth",
                                  block: "center",
                                });
                              }
                            }}
                          >
                            <div className="d-flex align-items-center gap-3 text-start">
                              <div
                                className={`rounded-circle d-flex align-items-center justify-content-center ${
                                  answered
                                    ? "bg-success text-white"
                                    : "border text-muted"
                                }`}
                                style={{ width: 32, height: 32 }}
                              >
                                {answered ? (
                                  <i className="bi bi-check" />
                                ) : (
                                  <span className="fw-semibold">{idx + 1}</span>
                                )}
                              </div>
                              <div>
                                <div
                                  className="fw-semibold text-truncate small"
                                  style={{ maxWidth: 180 }}
                                >
                                  {question.questionText || `Question ${idx + 1}`}
                                </div>
                              </div>
                            </div>
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : (
                <Card className="border-0 shadow-sm rounded-4">
                  <Card.Header className="border-0 bg-white px-4 pt-3 pb-2 fw-bold mb-2">
                    Questions
                  </Card.Header>
                  <Card.Body className="px-3 pt-0">
                    <ul className="list-unstyled mb-0">
                      {response?.template?.introHtml && response.template.introHtml.trim() && (
                        <li key="intro" className="mb-2">
                          <Button
                            variant="light"
                            className="d-flex align-items-center justify-content-between p-2 rounded-3 border bg-white w-100"
                            style={{ overflow: "hidden" }}
                            onClick={() => {
                              const node = questionRefs.current["intro"];
                              if (node) {
                                node.scrollIntoView({
                                  behavior: "smooth",
                                  block: "center",
                                });
                              }
                            }}
                          >
                            <div className="d-flex align-items-center gap-3 text-start">
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center border text-muted"
                                style={{ width: 32, height: 32 }}
                              >
                                <i className="bi bi-info-lg text-muted" />
                              </div>
                              <div>
                                <div
                                  className="fw-semibold text-truncate small"
                                  style={{ maxWidth: 180 }}
                                >
                                  Introduction
                                </div>
                              </div>
                            </div>
                          </Button>
                        </li>
                      )}
                      {numberedQuestions.map((question, idx) => {
                        const answered = isQuestionFilled(question);
                        const qId = question.id || `question-${idx}`;
                        return (
                          <li key={qId} className="mb-2">
                            <Button
                              variant="light"
                              className="d-flex align-items-center justify-content-between p-2 rounded-3 border bg-white w-100"
                              style={{ overflow: "hidden" }}
                              onClick={() => {
                                const node = questionRefs.current[qId];
                                if (node) {
                                  node.scrollIntoView({
                                    behavior: "smooth",
                                    block: "center",
                                  });
                                }
                              }}
                            >
                              <div className="d-flex align-items-center gap-3 text-start">
                                <div
                                  className={`rounded-circle d-flex align-items-center justify-content-center ${
                                    answered
                                      ? "bg-success text-white"
                                      : "border text-muted"
                                  }`}
                                  style={{ width: 32, height: 32 }}
                                >
                                  {answered ? (
                                    <i className="bi bi-check" />
                                  ) : (
                                    <span className="fw-semibold">{idx + 1}</span>
                                  )}
                                </div>
                                <div>
                                  <div
                                    className="fw-semibold text-truncate small"
                                    style={{ maxWidth: 180 }}
                                  >
                                    {question.questionText || `Question ${idx + 1}`}
                                  </div>
                                </div>
                              </div>
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  </Card.Body>
                </Card>
              )}
            </div>
          </Col>
          <Col lg={9} md={8} className={embedded ? "bg-inverse-light p-0 embeded-form-design-body-height overflow-y-auto" : ""}>
            {embedded ? (
              <div className="mb-4">
                {response?.template?.introHtml && (
                  <div
                    className="mb-4 mx-4 mt-4 p-3 rounded-3 bg-light border-0 eval-rich-content"
                    style={{ lineHeight: 1.6 }}
                    dangerouslySetInnerHTML={{ __html: response.template.introHtml }}
                    ref={(el) => {
                      if (el) {
                        questionRefs.current["intro"] = el;
                      }
                    }}
                  />
                )}
                {questions.map((question, index) => {
                  const questionNumber = numberedQuestions
                    .slice(0, index + 1)
                    .filter(
                      (q) =>
                        !["header-paragraph", "divider"].includes(q.questionType)
                    ).length;

                  return (
                    <div
                      key={`question-${question.id}-${index}`}
                      className="mb-5 pb-5 mt-4 px-4 eval-question-border"
                      ref={(el) => {
                        const key = question.id || `question-${index}`;
                        if (el) {
                          questionRefs.current[key] = el;
                        }
                      }}
                    >
                      <div>
                        {["header-paragraph", "divider"].includes(
                          question.questionType
                        ) ? (
                          question.questionType === "divider" ? (
                            <hr
                              style={{
                                borderTop: `2px ${question.style || "solid"} #ccc`,
                                margin: "32px 0",
                              }}
                            />
                          ) : (
                            <QuestionRenderer question={question} />
                          )
                        ) : (
                          <>
                            <div className="d-flex align-items-center gap-3 mb-4">
                              <span
                                className=" bg-light fw-semibold px-3 py-2 rounded-3"
                              >
                                Question {questionNumber}
                              </span>
                              <div>
                                <h5 className="fw-semibold mb-0">
                                  {question.questionText ||
                                    `Question ${questionNumber}`}
                                </h5>
                              </div>
                            </div>
                            {(question.showInfo || (question.infoHtml && String(question.infoHtml).trim())) && question.infoPlacement === "before" && (
                              <div className="mb-3 p-3 rounded-3 bg-light small eval-rich-content" style={{ lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: question.infoHtml || "" }} />
                            )}
                            <QuestionRenderer question={question} answer={response.answers?.[question.id]} />
                            {response.comments &&
                              response.comments[question.id] && (
                                <div className="mt-3 p-3 bg-light rounded">
                                  <div className="d-flex align-items-center mb-2">
                                    <i className="bi bi-chat-dots text-info me-2"></i>
                                    <strong className="text-info">
                                      Comment:
                                    </strong>
                                  </div>
                                  <p className="mb-0">
                                    {response.comments[question.id]}
                                  </p>
                                </div>
                              )}
                            {(question.showInfo || (question.infoHtml && String(question.infoHtml).trim())) && question.infoPlacement === "after" && (
                              <div className="mt-3 p-3 rounded-3 bg-light small eval-rich-content" style={{ lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: question.infoHtml || "" }} />
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div className="d-flex justify-content-end py-3 px-4">
                  <Button onClick={() => navigate(backPath)} variant="primary">
                    Back to Dashboard
                  </Button>
                </div>
              </div>
            ) : (
              <Card className="mb-4 shadow-sm border-0">
                <Card.Body className="p-0">
                  {response?.template?.introHtml && (
                    <div
                      className="mb-4 mx-4 mt-4 p-3 rounded-3 bg-light border-0 eval-rich-content"
                      style={{ lineHeight: 1.6 }}
                      dangerouslySetInnerHTML={{ __html: response.template.introHtml }}
                      ref={(el) => {
                        if (el) {
                          questionRefs.current["intro"] = el;
                        }
                      }}
                    />
                  )}
                  {questions.map((question, index) => {
                    const questionNumber = numberedQuestions
                      .slice(0, index + 1)
                      .filter(
                        (q) =>
                          !["header-paragraph", "divider"].includes(q.questionType)
                      ).length;

                    return (
                      <div
                        key={`question-${question.id}-${index}`}
                        className="mb-5 pb-5 mt-4 px-4 eval-question-border"
                        ref={(el) => {
                          const key = question.id || `question-${index}`;
                          if (el) {
                            questionRefs.current[key] = el;
                          }
                        }}
                      >
                        <div>
                          {["header-paragraph", "divider"].includes(
                            question.questionType
                          ) ? (
                            question.questionType === "divider" ? (
                              <hr
                                style={{
                                  borderTop: `2px ${question.style || "solid"} #ccc`,
                                  margin: "32px 0",
                                }}
                              />
                            ) : (
                              <QuestionRenderer question={question} />
                            )
                          ) : (
                            <>
                              <div className="d-flex align-items-center gap-3 mb-4">
                                <span
                                  className=" bg-light fw-semibold px-3 py-2 rounded-3"
                                >
                                  Question {questionNumber}
                                </span>
                                <div>
                                  <h5 className="fw-semibold mb-0">
                                    {question.questionText ||
                                      `Question ${questionNumber}`}
                                  </h5>
                                </div>
                              </div>
                              {(question.showInfo || (question.infoHtml && String(question.infoHtml).trim())) && question.infoPlacement === "before" && (
                                <div className="mb-3 p-3 rounded-3 bg-light small eval-rich-content" style={{ lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: question.infoHtml || "" }} />
                              )}
                              <QuestionRenderer question={question} answer={response.answers?.[question.id]} />
                              {response.comments &&
                                response.comments[question.id] && (
                                  <div className="mt-3 p-3 bg-light rounded">
                                    <div className="d-flex align-items-center mb-2">
                                      <i className="bi bi-chat-dots text-info me-2"></i>
                                      <strong className="text-info">
                                        Comment:
                                      </strong>
                                    </div>
                                    <p className="mb-0">
                                      {response.comments[question.id]}
                                    </p>
                                  </div>
                                )}
                              {(question.showInfo || (question.infoHtml && String(question.infoHtml).trim())) && question.infoPlacement === "after" && (
                                <div className="mt-3 p-3 rounded-3 bg-light small eval-rich-content" style={{ lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: question.infoHtml || "" }} />
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </Card.Body>
                <Card.Footer
                  className="d-flex justify-content-end py-3 px-4"
                  style={{
                    borderTop: "1px dashed var(--border-bg)",
                    backgroundColor: "var(--bs-card-bg)",
                  }}
                >
                  <Button onClick={() => navigate(backPath)} variant="primary">
                    Back to Dashboard
                  </Button>
                </Card.Footer>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EvaluationResponseView; 